<template>
  <ShortcutWrapper @n-shortcut="onNShortcut" isDialog>
    <DetailCard :title="$t('speciesGroups')" :style="{ borderRadius: '0px 4px 4px 4px' }">
      <template #title.append>
        <v-row
        v-if="crud"
        no-gutters
        justify="end">
          <Icon
          icon="mdi-plus"
          :tooltipText="$t('new')"
          @icon-clicked="newSpeciesGroupClicked()"
          margin=""
          :small="false"
          tabindex="0"/>
        </v-row>
      </template>

      <v-data-table
      class="small-headers"
      :loading="loading"
      dense
      :items="accountSpeciesGroups"
      :items-per-page="-1"
      hide-default-footer
      fixed-header
      :height="tableHeight"
      :headers="speciesGroupHeaders">

        <template #item.species="{ item }">
          <v-tooltip bottom color="secondary"
            v-for="species in item.species"
            :key="species.enum">
            <template #activator="{ on }">
              <v-chip
              v-on="on"
              :small="item.species.length > 3 && item.species.length <= 6"
              :x-small="item.species.length > 6"
              class="my-1 mr-1">
                <span>{{species.commonName}}</span>
              </v-chip>
            </template>
            <span class="subtitle-1 white--text"> {{species.code}} </span>
            <br>
            <span class="subtitle-1 white--text font-italic"> {{species.speciesName}} </span>
          </v-tooltip>
        </template>

        <template #item.lengthGroups="{ item }">
          <v-tooltip bottom color="secondary"
            v-for="lengthGroup in item.lengthGroups"
            :key="lengthGroup.lengthGroupId">
            <template #activator="{ on }">
              <v-chip
              v-on="on"
              :small="item.lengthGroups.length > 3 && item.lengthGroups.length <= 6"
              :x-small="item.lengthGroups.length > 6"
              class="my-1 mr-1">
                <span>{{lengthGroup.label}}</span>
              </v-chip>
            </template>
            <span class="subtitle-1 white--text"> {{lengthGroup.logLengths.map(l => `${l.length}\'`).join(', ')}} </span>
          </v-tooltip>
        </template>

        <template #item.diameterGroups="{ item }">
          <v-tooltip bottom color="secondary"
          v-for="diameterGroup in item.diameterGroups"
          :key="diameterGroup.diameterGroupId">
            <template #activator="{ on }">

              <v-chip
              v-on="on"
              :small="item.diameterGroups.length > 3 && item.diameterGroups.length <= 6"
              :x-small="item.diameterGroups.length > 6"
              class="my-1 mr-1">
                <span>{{diameterGroup.label}}</span>
              </v-chip>

            </template>
            <span class="subtitle-1 white--text"> {{`${diameterGroup.lowerBound}" \u2014 ${diameterGroup.upperBound}"`}} </span>
          </v-tooltip>

        </template>

        <template #item.actions="{ item }">
          <v-row v-if="!!item.label" no-gutters justify="end">
            <Icon
            icon="mdi-content-copy"
            :tooltipText="$t('duplicate')"
            @icon-clicked="duplicateSpeciesGroupClicked(item)"/>
            <Icon
            icon="mdi-pencil"
            :tooltipText="$t('edit')"
            @icon-clicked="editSpeciesGroupClicked(item)"/>
            <Icon
            icon="mdi-delete-forever"
            :tooltipText="$t('delete')"
            @icon-clicked="deleteSpeciesGroupClicked(item)"
            margin=""/>
          </v-row>
        </template>
      </v-data-table>

      <Dialog
      :stateId="dialogId"
      @dialog-closing="handleCancelEdit"
      @click:outside="handleCancelEdit"
      @close="handleCancelEdit">
        <AccountSpeciesGroupForm
        :speciesGroup="focusedSpeciesGroup"
        :account="account"
        @save="handleSaveSpeciesGroup"
        @cancel="handleCancelEdit"/>
      </Dialog>
    </DetailCard>
  </ShortcutWrapper>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { speciesGroupHeaders } from '@/headers/Account'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'

export default {
  name: 'AccountSpeciesGroups',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    AccountSpeciesGroupForm: () => import('./AccountSpeciesGroupForm.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    account: {
      type: Object,
      required: true
    }
  },

  async mounted () {
    await this.refresh()
  },

  data: () => ({
    rules: fieldRules.rules,
    dialogId: uniqueDialogId('account-species-group'),
    loading: false,
    focusedSpeciesGroup: null
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountSpeciesGroups']),
    ...mapGetters('dialog', ['topDialogState', 'dialogStateById']),
    ...mapGetters('species', ['allSpecies', 'speciesLoading']),

    accountId () {
      return this.account?.accountId
    },

    tableHeight () {
      const { fullscreen } = this.topDialogState
      const dialogHeightPcnt = fullscreen ? 100 : 90
      const dialogBufferSpace = 350
      return `max(${256}px, min(${800}px, calc(${dialogHeightPcnt}vh - ${dialogBufferSpace}px)))`
    },

    speciesGroupHeaders () {
      return speciesGroupHeaders(this.crud)
    }
  },

  methods: {
    ...mapActions('account-scaling', ['fetchAccountSpeciesGroups', 'createAccountSpeciesGroup',
      'updateAccountSpeciesGroup', 'deleteAccountSpeciesGroup']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('species', ['fetchSpecies']),

    async refresh () {
      this.loading = true
      if (this.accountId) {
        await this.fetchSpeciesGroups()
      }
      this.loading = false
    },

    async fetchAllSpecies () {
      await this.fetchSpecies()
    },

    async fetchSpeciesGroups () {
      await this.fetchAccountSpeciesGroups(this.accountId)
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.SimpleWide })
    },

    handleCancelEdit () {
      this.focusedSpeciesGroup = null
      this.closeDialog()
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async newSpeciesGroupClicked () {
      await this.fetchAllSpecies()
      this.openDialog()
    },

    async duplicateSpeciesGroupClicked (speciesGroup) {
      const { lengthGroups, diameterGroups } = speciesGroup
      this.focusedSpeciesGroup = { lengthGroups, diameterGroups }
      await this.fetchAllSpecies()
      this.openDialog()
    },

    async editSpeciesGroupClicked (speciesGroup) {
      this.focusedSpeciesGroup = speciesGroup
      await this.fetchAllSpecies()
      this.openDialog()
    },

    async handleSaveSpeciesGroup (speciesGroup) {
      if (speciesGroup.speciesGroupId) {
        await this.updateSpeciesGroupOnServer(speciesGroup)
      } else {
        await this.createSpeciesGroupOnServer(speciesGroup)
      }
      this.focusedSpeciesGroup = null
      await this.fetchSpeciesGroups()
      this.closeDialog()
    },

    async deleteSpeciesGroupClicked (speciesGroup) {
      await this.deleteSpeciesGroupOnServer(speciesGroup)
    },

    async createSpeciesGroupOnServer (speciesGroup) {
      await this.createAccountSpeciesGroup({
        accountId: this.accountId,
        speciesIds: speciesGroup.species.map(s => s.speciesId),
        lengthGroupIds: speciesGroup.lengthGroups.map(lg => lg.lengthGroupId),
        diameterGroupIds: speciesGroup.diameterGroups.map(dg => dg.diameterGroupId),
        ...speciesGroup
      })
      await this.fetchSpeciesGroups()
      this.emitSpeciesGroupMutated()
    },

    async updateSpeciesGroupOnServer (speciesGroup) {
      await this.updateAccountSpeciesGroup({
        accountId: this.accountId,
        speciesIds: speciesGroup.species.map(s => s.speciesId),
        lengthGroupIds: speciesGroup.lengthGroups.map(lg => lg.lengthGroupId),
        diameterGroupIds: speciesGroup.diameterGroups.map(dg => dg.diameterGroupId),
        ...speciesGroup
      })
      await this.fetchSpeciesGroups()
      this.emitSpeciesGroupMutated()
    },

    async deleteSpeciesGroupOnServer (speciesGroup) {
      await this.deleteAccountSpeciesGroup({
        accountId: this.accountId,
        ...speciesGroup
      })
      await this.fetchSpeciesGroups()
      this.emitSpeciesGroupMutated()
    },

    onNShortcut () {
      if (this.childDialogIsOpen()) {
        return
      }
      this.newSpeciesGroupClicked()
    },

    childDialogIsOpen () {
      return !!this.dialogStateById(this.dialogId)
    },

    emitSpeciesGroupMutated () {
      this.$emit('account-species-group-mutated')
    }
  }
}
</script>
